import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    
    useEffect(() => {
        if (token) {
            validateToken();
        } else {
            setIsValidating(false);
        }
    }, [token]);
    
    const validateToken = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
            setTokenValid(true);
            setTokenEmail(response.data.email);
        } catch (error) {
            setTokenValid(false);
        } finally {
            setIsValidating(false);
        }
    };
    
    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        setIsLoading(true);
        try {
            await axios.post(`${API_URL}/api/auth/reset-password`, {
                token,
                new_password: newPassword
            });
            setResetSuccess(true);
            toast.success('Password reset successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };
    
    if (isValidating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
        );
    }
    
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] p-4">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md"
            >
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                    <span className="font-serif text-xl font-semibold text-amber-600">BestWillsAndTrusts</span>
                </div>
                
                {!token ? (
                    <Card className="border-stone-200 shadow-sm rounded-none">
                        <CardHeader className="space-y-1">
                            <div className="flex items-center gap-2 text-red-600 mb-2">
                                <XCircle className="h-5 w-5" />
                            </div>
                            <CardTitle className="font-serif text-2xl">Invalid Link</CardTitle>
                            <CardDescription>
                                No reset token provided. Please request a new password reset link.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="w-full rounded-none font-medium"
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                ) : !tokenValid ? (
                    <Card className="border-stone-200 shadow-sm rounded-none">
                        <CardHeader className="space-y-1">
                            <div className="flex items-center gap-2 text-red-600 mb-2">
                                <XCircle className="h-5 w-5" />
                            </div>
                            <CardTitle className="font-serif text-2xl">Link Expired</CardTitle>
                            <CardDescription>
                                This password reset link is invalid or has expired. Please request a new one.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="w-full rounded-none font-medium"
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                ) : resetSuccess ? (
                    <Card className="border-stone-200 shadow-sm rounded-none">
                        <CardHeader className="space-y-1">
                            <div className="flex items-center gap-2 text-green-600 mb-2">
                                <CheckCircle className="h-5 w-5" />
                            </div>
                            <CardTitle className="font-serif text-2xl">Password Reset!</CardTitle>
                            <CardDescription>
                                Your password has been successfully reset. You can now log in with your new password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="w-full rounded-none font-medium uppercase tracking-wide"
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="border-stone-200 shadow-sm rounded-none">
                        <CardHeader className="space-y-1">
                            <div className="flex items-center gap-2 text-amber-600 mb-2">
                                <Lock className="h-5 w-5" />
                            </div>
                            <CardTitle className="font-serif text-2xl">Create New Password</CardTitle>
                            <CardDescription>
                                Enter a new password for <strong>{tokenEmail}</strong>
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleResetPassword} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="new-password">New Password</Label>
                                    <Input
                                        id="new-password"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        className="rounded-none"
                                        data-testid="new-password-input"
                                    />
                                    <p className="text-xs text-stone-500">
                                        Min 8 characters with uppercase, lowercase, number & special character
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password">Confirm Password</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="rounded-none"
                                        data-testid="confirm-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full rounded-none font-medium uppercase tracking-wide"
                                    disabled={isLoading}
                                    data-testid="reset-submit-btn"
                                >
                                    {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                    {isLoading ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                )}
            </motion.div>
        </div>
    );
};

export default ResetPassword;
